import { useEffect, useCallback, useRef } from 'react';

const HEIGHT_THRESHOLD = 2;

/**
 * Measure the true content extent by scanning visible leaf-ish elements
 * inside .App-content and returning the bottom-most pixel position.
 *
 * We deliberately skip direct children of .App-content (page-level
 * wrappers like PageContainer that carry min-height: 100vh) and instead
 * look at their descendants, whose positions are purely content-driven.
 */
function getContentHeight() {
  const appContent = document.querySelector('.App-content');
  if (!appContent) {
    const root = document.getElementById('root');
    return root ? root.scrollHeight : document.body.scrollHeight;
  }

  // Scan all rendered elements inside .App-content and find the
  // bottom-most visible pixel.  Elements stretched by viewport-relative
  // min-height are taller than their content, but their *children*
  // end where the real content ends.
  let maxBottom = 0;
  const elements = appContent.querySelectorAll('*');
  for (const el of elements) {
    // Skip elements that are purely layout containers (have children
    // and could be inflated by min-height / flex-grow).
    if (el.children.length > 0) continue;

    const rect = el.getBoundingClientRect();
    if (rect.height > 0 && rect.width > 0) {
      maxBottom = Math.max(maxBottom, rect.bottom);
    }
  }

  return Math.ceil(maxBottom) || document.body.scrollHeight;
}

/**
 * Hook to communicate iframe content height to parent window.
 *
 * Sends postMessage with content height so parent can resize iframe
 * dynamically.  Used for embedded checkout scenarios.
 *
 * @param {Object}  options
 * @param {boolean} options.enabled     - Enable resize messaging (default true)
 * @param {number}  options.debounceMs  - Debounce delay in ms (default 150)
 * @param {number}  options.padding     - Extra height in px (default 20)
 */
export const useIframeResize = ({
  enabled = true,
  debounceMs = 150,
  padding = 20,
} = {}) => {
  const timeoutRef = useRef(null);
  const lastHeightRef = useRef(0);
  const suppressUntilRef = useRef(0);

  const sendHeight = useCallback((height) => {
    if (!enabled) return;
    if (Math.abs(height - lastHeightRef.current) <= HEIGHT_THRESHOLD) return;
    lastHeightRef.current = height;

    suppressUntilRef.current = Date.now() + 500;

    try {
      window.parent.postMessage({
        type: "iframeResize",
        height: height,
      }, "*");
    } catch (err) {
      console.warn("Failed to send iframe resize message:", err);
    }
  }, [enabled]);

  const updateHeight = useCallback(() => {
    if (!enabled) return;
    if (Date.now() < suppressUntilRef.current) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const height = getContentHeight();
      sendHeight(height + padding);
    }, debounceMs);
  }, [enabled, debounceMs, padding, sendHeight]);

  const forceUpdate = useCallback(() => {
    if (!enabled) return;
    const height = getContentHeight();
    sendHeight(height + padding);
  }, [enabled, padding, sendHeight]);

  // ── Style overrides ──────────────────────────────────────────────
  // Collapse the entire html ▸ body ▸ #root ▸ .App ▸ .App-content
  // chain so nothing stretches to the iframe viewport.
  useEffect(() => {
    if (!enabled) return;

    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const app  = document.querySelector('.App');
    const appContent = document.querySelector('.App-content');

    const set = (el, prop, val) =>
      el?.style.setProperty(prop, val, 'important');

    set(html, 'overflow',          'hidden');
    set(html, 'height',            'auto');
    set(html, 'min-height',        '0');
    set(html, 'background-color',  'transparent');

    set(body, 'overflow',          'hidden');
    set(body, 'margin',            '0');
    set(body, 'padding',           '0');
    set(body, 'height',            'auto');
    set(body, 'min-height',        '0');
    set(body, 'background-color',  'transparent');

    set(root, 'height',            'auto');
    set(root, 'min-height',        '0');
    set(root, 'background-color',  'transparent');

    set(app, 'padding',            '0');
    set(app, 'height',             'auto');
    set(app, 'min-height',         '0');
    set(app, 'background-color',   'transparent');

    set(appContent, 'height',          'auto');
    set(appContent, 'min-height',      '0');
    set(appContent, 'background-color','transparent');

    // Styled-component page wrappers (e.g. PageContainer) sit as direct
    // children of .App-content and often carry min-height: 100vh.
    // Collapse them so the ResizeObserver sees content-driven height.
    const pageWrappers = appContent ? [...appContent.children] : [];
    for (const wrapper of pageWrappers) {
      set(wrapper, 'min-height', '0');
      set(wrapper, 'height', 'auto');
    }

    return () => {
      const clear = (el, prop) => el?.style.removeProperty(prop);
      for (const el of [html, body, root, app, appContent]) {
        clear(el, 'overflow');
        clear(el, 'height');
        clear(el, 'min-height');
        clear(el, 'margin');
        clear(el, 'padding');
        clear(el, 'background-color');
      }
      for (const wrapper of pageWrappers) {
        clear(wrapper, 'min-height');
        clear(wrapper, 'height');
      }
    };
  }, [enabled]);

  // ── Observers ────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    const initialTimeout = setTimeout(forceUpdate, 300);

    // Observe .App-content (the direct content wrapper) rather than
    // #root or body, which resize when the SDK changes the iframe.
    const observeTarget =
      document.querySelector('.App-content') ||
      document.getElementById('root') ||
      document.body;

    const resizeObserver = new ResizeObserver(() => updateHeight());
    resizeObserver.observe(observeTarget);

    // Only watch structural DOM changes (new/removed nodes).
    // Attribute mutations (style, class) fire constantly when the
    // payment SDK updates focus / hover / validation states and would
    // re-trigger measurement on every interaction.
    const mutationObserver = new MutationObserver(() => updateHeight());
    mutationObserver.observe(observeTarget, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(initialTimeout);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [enabled, updateHeight, forceUpdate]);

  return { updateHeight, forceUpdate, sendHeight };
};

export default useIframeResize;
